import { createSelector } from 'reselect';
import { Language } from '@/types';
import { RootState } from '../types';
import { FilterItem } from '@/types/api-definitions';
import {
  collectTagsInPreferredLanguage,
  collectAllTagsWithFallback,
  convertToFilterItems,
} from './helpers';

export const companiesFilterItemsSelector = createSelector(
  [(state: RootState) => state.api.apiDefinitions],
  (apis): FilterItem[] => {
    const companyCount = apis.reduce((map, api) => {
      const companyName = api.company?.name;
      if (companyName) {
        map.set(companyName, (map.get(companyName) ?? 0) + 1);
      }
      return map;
    }, new Map<string, number>());

    return Array.from(companyCount.entries())
      .map(([name, count]) => ({
        slug: name,
        name,
        count,
      }))
      .sort((a, b) => a.slug.localeCompare(b.slug));
  },
);

export const categoriesFilterItemsSelector = (selectedLanguage: Language) =>
  createSelector(
    [(state: RootState) => state.api?.apiDefinitions, () => selectedLanguage],
    (apiDefinitions, language): FilterItem[] => {
      if (!apiDefinitions) return [];

      const preferredNames = collectTagsInPreferredLanguage(apiDefinitions, language);
      const tagsBySlug = collectAllTagsWithFallback(apiDefinitions, language, preferredNames);
      return convertToFilterItems(tagsBySlug);
    },
  );

export const accessFilterItemsSelector = (t: (key: string) => string) =>
  createSelector(
    [(state: RootState) => state.api?.apiDefinitions, () => t],
    (apiDefinitions, translationFn): FilterItem[] => {
      if (!apiDefinitions) return [];

      const { publicCount, privateCount } = apiDefinitions.reduce(
        (counts, apiDefinition) => {
          if (apiDefinition.isPublic) {
            counts.publicCount++;
          } else {
            counts.privateCount++;
          }
          return counts;
        },
        { publicCount: 0, privateCount: 0 },
      );

      const result: FilterItem[] = [];

      if (publicCount > 0) {
        result.push({
          slug: 'public',
          name: translationFn('apiFilter.access.public'),
          count: publicCount,
        });
      }

      if (privateCount > 0) {
        result.push({
          slug: 'private',
          name: translationFn('apiFilter.access.private'),
          count: privateCount,
        });
      }

      return result.sort((a, b) => a.slug.localeCompare(b.slug));
    },
  );
